import React, { useMemo, useState, useRef, useEffect, useContext } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { LanguageContext } from "./LanguageContext";
import RetirementCalculatorHelpContent from "./content/retirementCalculatorHelpContent";

/**
 * Retirement Calculator (React)
 * - Calculates target portfolio using withdrawal rate scenarios (4%, 3.5%, 3%)
 * - Optionally subtracts Social Security (or any guaranteed income)
 * - Estimates monthly contribution needed to reach target in N years
 *
 * Notes:
 * - expectedReturn is nominal annual return (e.g., 0.06 = 6%)
 * - This is a planning calculator; does not include taxes/healthcare/fees.
 */

const DEFAULT_WITHDRAWAL_RATES = [0.04, 0.035, 0.03];

function formatUSD(value) {
  if (!Number.isFinite(value)) return "—";
  return value.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function clampNonNeg(n) {
  return Math.max(0, Number.isFinite(n) ? n : 0);
}

function calcResults({
  monthlySpend,
  includeSocialSecurity,
  socialSecurityMonthly,
  currentInvested,
  yearsToRetire,
  expectedReturn,
  withdrawalRates = DEFAULT_WITHDRAWAL_RATES,
}) {
  const annualSpend = clampNonNeg(monthlySpend) * 12;

  const guaranteedAnnual = includeSocialSecurity
    ? clampNonNeg(socialSecurityMonthly) * 12
    : 0;

  const gapAnnual = Math.max(0, annualSpend - guaranteedAnnual);

  const nMonths = Math.max(0, Math.round(clampNonNeg(yearsToRetire) * 12));
  const rMonthly = clampNonNeg(expectedReturn) / 12;

  const fvFactor = nMonths > 0 ? Math.pow(1 + rMonthly, nMonths) : 1;

  return withdrawalRates.map((wr) => {
    const target = wr > 0 ? gapAnnual / wr : 0;

    // Monthly contribution needed:
    // target = current*(1+r)^n + c * [((1+r)^n - 1)/r]
    // c = (target - current*(1+r)^n) * r / ((1+r)^n - 1)
    let monthlyContribution = 0;

    const curr = clampNonNeg(currentInvested);

    if (nMonths > 0 && rMonthly > 0) {
      const numerator = (target - curr * fvFactor) * rMonthly;
      const denom = fvFactor - 1;
      monthlyContribution = denom > 0 ? numerator / denom : 0;
    } else if (nMonths > 0 && rMonthly === 0) {
      // No growth: linear savings
      monthlyContribution = (target - curr) / nMonths;
    } else {
      monthlyContribution = 0;
    }

    monthlyContribution = Math.max(0, monthlyContribution);

    return {
      withdrawalRate: wr,
      annualSpend,
      guaranteedAnnual,
      gapAnnual,
      target,
      currentInvested: curr,
      shortfall: Math.max(0, target - curr),
      yearsToRetire: nMonths / 12,
      monthlyContribution,
    };
  });
}

export default function RetirementCalculator() {
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const { language } = useContext(LanguageContext);

  const [monthlySpend, setMonthlySpend] = useState(5000); // desired retirement spend
  const [includeSS, setIncludeSS] = useState(true);
  const [socialSecurityMonthly, setSocialSecurityMonthly] = useState(2071);

  const [currentInvested, setCurrentInvested] = useState(100000);
  const [yearsToRetire, setYearsToRetire] = useState(20);
  const [expectedReturn, setExpectedReturn] = useState(0.06); // 6%

  const scrollRef = useRef(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= 768
  );
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogin = () => {
    localStorage.setItem("auth_redirect_path", window.location.pathname);
    loginWithRedirect();
  };

  const scrollResults = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = scrollRef.current.offsetWidth * 0.85;
      scrollRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const results = useMemo(
    () =>
      calcResults({
        monthlySpend,
        includeSocialSecurity: includeSS,
        socialSecurityMonthly,
        currentInvested,
        yearsToRetire,
        expectedReturn,
      }),
    [monthlySpend, includeSS, socialSecurityMonthly, currentInvested, yearsToRetire, expectedReturn]
  );

  return (
    <div style={{
      minHeight: "100vh",
      background: "radial-gradient(900px 400px at 20% 10%, rgba(190, 5, 45, 0.07), transparent 60%), radial-gradient(900px 420px at 85% 35%, rgba(36,85,128,0.18), transparent 55%), linear-gradient(180deg, #ffffff, #f7f9fb)",
      borderBottom: "1px solid rgba(0,0,0,0.08)",
      padding: "40px 20px",
      position: "relative",
    }}>
      <style>{`
        .results-scroll::-webkit-scrollbar { display: none; }
      `}</style>

      <div style={{ maxWidth: 980, margin: "0 auto", fontFamily: "Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif" }}>
        {/* Title and description - always visible */}
        <h2 style={{
          margin: "0 0 10px",
          fontFamily: "Inter, Helvetica Neue, Arial, sans-serif",
          fontWeight: 900,
          color: "#0b1220",
          letterSpacing: "-0.02em",
        }}>
          {language === "pt" ? "Calculadora: Seu Número da Aposentadoria (EUA)" : "Calculator: Your Retirement Number (USA)"}
        </h2>
        <p style={{
          marginTop: 0,
          marginBottom: 24,
          color: "#5b6775",
          fontFamily: "'Open Sans', system-ui, sans-serif",
          lineHeight: 1.7,
        }}>
          {language === "pt"
            ? "Estimativa inicial baseada em taxa de saque (4%, 3,5%, 3%). Não inclui impostos, saúde, taxas ou mudanças de padrão de vida."
            : "Initial estimate based on withdrawal rate (4%, 3.5%, 3%). Does not include taxes, healthcare, fees, or lifestyle changes."}
        </p>

        {/* Login Required Overlay - shown when not authenticated */}
        {!isAuthenticated && (
          <div style={{
            background: "rgba(255,255,255,0.95)",
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: 16,
            padding: "32px 24px",
            boxShadow: "0 10px 26px rgba(0,0,0,0.06)",
            backdropFilter: "blur(8px)",
            marginBottom: 24,
            textAlign: "center",
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
            <h3 style={{
              margin: "0 0 12px 0",
              fontFamily: "Inter, sans-serif",
              fontWeight: 800,
              color: "#245580",
              fontSize: "1.3rem",
            }}>
              {language === "pt" ? "Acesso Exclusivo para Membros" : "Members Only Access"}
            </h3>
            <p style={{
              margin: "0 0 20px 0",
              color: "#5b6775",
              fontFamily: "'Open Sans', sans-serif",
              fontSize: "1rem",
              lineHeight: 1.6,
              maxWidth: 420,
              marginLeft: "auto",
              marginRight: "auto",
            }}>
              {language === "pt"
                ? "Faça login ou crie sua conta gratuita para usar a calculadora de aposentadoria."
                : "Log in or create your free account to use the retirement calculator."}
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <button
                onClick={handleLogin}
                style={{
                  padding: "14px 28px",
                  borderRadius: 12,
                  border: "none",
                  background: "linear-gradient(90deg, #d83605, #ff5045)",
                  color: "#fff",
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 800,
                  fontSize: "1rem",
                  cursor: "pointer",
                  boxShadow: "0 8px 20px rgba(216, 54, 5, 0.25)",
                  transition: "transform 0.15s ease, box-shadow 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 12px 24px rgba(216, 54, 5, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 8px 20px rgba(216, 54, 5, 0.25)";
                }}
              >
                {language === "pt" ? "Entrar / Criar Conta" : "Login / Sign Up"}
              </button>
            </div>
          </div>
        )}

        {/* Calculator Content - blurred when not authenticated */}
        <div style={{
          filter: !isAuthenticated ? "blur(6px)" : "none",
          pointerEvents: !isAuthenticated ? "none" : "auto",
          userSelect: !isAuthenticated ? "none" : "auto",
        }}>
        <div style={{
          background: "rgba(255,255,255,0.9)",
          border: "1px solid rgba(0,0,0,0.08)",
          borderRadius: 16,
          padding: 20,
          boxShadow: "0 10px 26px rgba(0,0,0,0.06)",
          backdropFilter: "blur(8px)",
          marginBottom: 24,
        }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
            <Field
              label="Gasto mensal desejado na aposentadoria (USD)"
              value={monthlySpend}
              onChange={setMonthlySpend}
              step="100"
            />
            <Field
              label="Quanto você já tem investido hoje (USD)"
              value={currentInvested}
              onChange={setCurrentInvested}
              step="1000"
            />

            <Field
              label="Anos até se aposentar"
              value={yearsToRetire}
              onChange={setYearsToRetire}
              step="1"
            />
            <Field
              label="Retorno anual esperado (ex.: 0.06 = 6%)"
              value={expectedReturn}
              onChange={setExpectedReturn}
              step="0.005"
            />

            <div style={{
              gridColumn: "1 / -1",
              padding: 16,
              background: "rgba(255,255,255,0.75)",
              border: "1px solid rgba(0,0,0,0.08)",
              borderRadius: 14,
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 800, color: "#0b1220", fontFamily: "Inter, sans-serif" }}>Contar Social Security?</div>
                  <div style={{ fontSize: 13, color: "#5b6775", fontFamily: "'Open Sans', sans-serif" }}>Se desligado, o cálculo assume que você banca 100% do gasto com investimentos.</div>
                </div>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={includeSS}
                    onChange={(e) => setIncludeSS(e.target.checked)}
                    style={{ width: 18, height: 18, accentColor: "#245580" }}
                  />
                  <span style={{ fontWeight: 700, color: "#245580" }}>{includeSS ? "Sim" : "Não"}</span>
                </label>
              </div>

              {includeSS && (
                <div style={{ marginTop: 12 }}>
                  <Field
                    label="Social Security mensal estimado (USD)"
                    value={socialSecurityMonthly}
                    onChange={setSocialSecurityMonthly}
                    step="50"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <h3 style={{
          marginTop: 0,
          marginBottom: 16,
          fontFamily: "Inter, sans-serif",
          fontWeight: 800,
          color: "#245580",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}>
          Resultados
        </h3>

        <div style={{ position: "relative" }}>
          {/* Left Arrow */}
          <button
            onClick={() => scrollResults("left")}
            style={{
              display: isMobile ? "flex" : "none",
              position: "absolute",
              left: -12,
              top: "50%",
              transform: "translateY(-50%)",
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: "white",
              border: "1px solid rgba(36,85,128,0.2)",
              boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              zIndex: 10,
              color: "#245580",
              fontSize: 18,
              fontWeight: 700,
            }}
            aria-label="Previous"
          >
            &lt;
          </button>

          {/* Right Arrow */}
          <button
            onClick={() => scrollResults("right")}
            style={{
              display: isMobile ? "flex" : "none",
              position: "absolute",
              right: -12,
              top: "50%",
              transform: "translateY(-50%)",
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: "white",
              border: "1px solid rgba(36,85,128,0.2)",
              boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              zIndex: 10,
              color: "#245580",
              fontSize: 18,
              fontWeight: 700,
            }}
            aria-label="Next"
          >
            &gt;
          </button>

          <div
            ref={scrollRef}
            className="results-scroll"
            style={{
              display: isMobile ? "flex" : "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 16,
              overflowX: isMobile ? "auto" : "visible",
              scrollSnapType: isMobile ? "x mandatory" : "none",
              scrollBehavior: "smooth",
              paddingBottom: isMobile ? 8 : 0,
              msOverflowStyle: "none",
              scrollbarWidth: "none",
            }}
          >
            {results.map((r) => (
              <div key={r.withdrawalRate} style={{
                background: "rgba(255,255,255,0.9)",
                border: "1px solid rgba(0,0,0,0.08)",
                borderRadius: 16,
                padding: 18,
                boxShadow: "0 10px 26px rgba(0,0,0,0.06)",
                transition: "transform 180ms ease, box-shadow 180ms ease",
                minWidth: isMobile ? "85%" : "auto",
                flexShrink: 0,
                scrollSnapAlign: isMobile ? "start" : "none",
              }}>
                <div style={{
                  fontWeight: 900,
                  marginBottom: 10,
                  color: "#d83605",
                  fontFamily: "Inter, sans-serif",
                }}>
                  Taxa de saque: {(r.withdrawalRate * 100).toFixed(1)}%
                </div>

                <Row label="Gasto anual" value={formatUSD(r.annualSpend)} />
                <Row label="Renda garantida anual" value={formatUSD(r.guaranteedAnnual)} />
                <Row label="Sai da carteira (gap)" value={formatUSD(r.gapAnnual)} />

                <hr style={{ border: 0, borderTop: "1px solid rgba(0,0,0,0.08)", margin: "12px 0" }} />

                <Row label="Patrimônio alvo" value={formatUSD(r.target)} strong accent />
                <Row label="Você já tem" value={formatUSD(r.currentInvested)} />
                <Row label="Falta" value={formatUSD(r.shortfall)} />
                <Row label="Aporte mensal estimado" value={formatUSD(r.monthlyContribution)} strong accent />
              </div>
            ))}
          </div>
        </div>
        </div>
        {/* End of blur container */}

        {/* Learn More Section - always visible */}
        <div style={{
          marginTop: 32,
          textAlign: "center",
          padding: "24px 16px",
          background: "rgba(255,255,255,0.75)",
          border: "1px solid rgba(0,0,0,0.08)",
          borderRadius: 14,
        }}>
          <p style={{
            margin: "0 0 14px 0",
            color: "#5b6775",
            fontFamily: "'Open Sans', sans-serif",
            fontSize: 14,
          }}>
            {language === "pt"
              ? "Quer entender melhor como funciona cada campo e resultado?"
              : "Want to understand how each field and result works?"}
          </p>
          <button
            onClick={() => setShowHelp(true)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "12px 24px",
              borderRadius: 999,
              border: "none",
              background: "linear-gradient(90deg, #d83605, #ff5045)",
              color: "#fff",
              fontFamily: "Inter, sans-serif",
              fontWeight: 800,
              fontSize: 14,
              cursor: "pointer",
              boxShadow: "0 8px 20px rgba(216, 54, 5, 0.25)",
              transition: "transform 0.15s ease, box-shadow 0.15s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 12px 24px rgba(216, 54, 5, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 8px 20px rgba(216, 54, 5, 0.25)";
            }}
          >
            <span>{language === "pt" ? "Como funciona?" : "How does it work?"}</span>
            <span style={{ fontSize: 16 }}>→</span>
          </button>
        </div>

        {/* Help Modal */}
        {showHelp && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
              backdropFilter: "blur(4px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: 20,
            }}
            onClick={() => setShowHelp(false)}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: 16,
                maxWidth: 680,
                width: "100%",
                maxHeight: "85vh",
                overflow: "hidden",
                boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
                display: "flex",
                flexDirection: "column",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "18px 24px",
                borderBottom: "1px solid rgba(0,0,0,0.08)",
                background: "rgba(36,85,128,0.04)",
              }}>
                <h3 style={{
                  margin: 0,
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 800,
                  color: "#245580",
                  fontSize: 18,
                }}>
                  {language === "pt" ? "Como usar a calculadora" : "How to use the calculator"}
                </h3>
                <button
                  onClick={() => setShowHelp(false)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: 24,
                    color: "#5b6775",
                    cursor: "pointer",
                    padding: "4px 8px",
                    lineHeight: 1,
                  }}
                  aria-label="Fechar"
                >
                  ×
                </button>
              </div>

              {/* Modal Content */}
              <div style={{
                padding: "20px 24px",
                overflowY: "auto",
                flex: 1,
              }}>
                <RetirementCalculatorHelpContent />
              </div>

              {/* Modal Footer */}
              <div style={{
                padding: "16px 24px",
                borderTop: "1px solid rgba(0,0,0,0.08)",
                textAlign: "right",
              }}>
                <button
                  onClick={() => setShowHelp(false)}
                  style={{
                    padding: "10px 20px",
                    borderRadius: 8,
                    border: "none",
                    background: "#245580",
                    color: "#fff",
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 700,
                    fontSize: 14,
                    cursor: "pointer",
                  }}
                >
                  {language === "pt" ? "Entendi" : "Got it"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, step = "1" }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{
        fontSize: 13,
        color: "#0b1220",
        marginBottom: 6,
        fontWeight: 700,
        fontFamily: "Inter, sans-serif",
      }}>
        {label}
      </div>
      <input
        type="number"
        value={value}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{
          width: "100%",
          padding: "12px 14px",
          borderRadius: 12,
          border: "1px solid rgba(0,0,0,0.08)",
          outline: "none",
          fontFamily: "'Open Sans', sans-serif",
          fontSize: 14,
          background: "rgba(255,255,255,0.75)",
          boxSizing: "border-box",
        }}
      />
    </label>
  );
}

function Row({ label, value, strong, accent }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginTop: 8 }}>
      <div style={{
        color: "#5b6775",
        fontSize: 13,
        fontFamily: "'Open Sans', sans-serif",
      }}>
        {label}
      </div>
      <div style={{
        fontWeight: strong ? 900 : 600,
        color: accent ? "#245580" : "#0b1220",
        fontFamily: "Inter, sans-serif",
      }}>
        {value}
      </div>
    </div>
  );
}
